import { Link } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { faqJsonLd, useSeo } from "../../lib/useSeo";
import { PilotApplicationForm } from "./PilotApplicationForm";

/**
 * The /pilot landing page — what we link to in cold-outreach to
 * property operators across multifamily, self-storage, parking, and
 * RV/boat.
 *
 * NOTE (2026-11-01): the launch pilot (year-free + price-locked-for-
 * life, by application) is CLOSED. The 30-day no-card trial is now the
 * only offer, and migration is included on every plan with no setup
 * fee. This page is KEPT at /pilot — bookmarks, cold-outreach links,
 * and SEO still land here — but repurposed to the "switch to NestPlot,
 * migration included" story. The application form stays (same endpoint,
 * POST /public/pilot-applications) as a "plan my migration" lead form;
 * operators can also just start a trial at /signup with no application.
 *
 * The 4 per-vertical pages (/pilot/multifamily, /pilot/storage,
 * /pilot/parking, /pilot/marina) reuse this page's shape — keep copy in
 * sync. PilotApplicationForm is shared.
 */

const FAQ = [
  [
    "What does it cost to get started?",
    "Nothing to try it. NestPlot is a 30-day free trial with no card required. After the trial it's flat pricing from $59/mo — Starter $59, Growth $99, Portfolio $199, or Enterprise for the largest portfolios (see /pricing). Your tenants, residents, boaters, or permit-holders pay you exactly what they pay today — NestPlot never touches that money.",
  ],
  [
    "Is migration really included?",
    "Yes — on every plan, with no setup fee. The NestPlot team runs the data import from whatever you're on today, so you're not left exporting spreadsheets alone. Migration help is part of the subscription, not an upsell.",
  ],
  [
    "What's involved in setup?",
    "Our team handles the data import from whatever you're on today — AppFolio, Yardi, DoorLoop, storEDGE, SiteLink, Easy Storage Solutions, Marina Office Manager, ParkMobile exports, or a spreadsheet — plus the per-state lease-template review, the access-vendor handshake, the Stripe Connect wiring, and the SMS opt-ins. Most setups complete in a single 60–90 minute working session.",
  ],
  [
    "What happens after the 30-day trial?",
    "Billing kicks in at your standard plan rate (see /pricing — flat from $59/mo: Starter $59, Growth $99, Portfolio $199, or Enterprise for the largest portfolios). You can cancel any time without owing for unused time; your customer data exports as CSV and is yours.",
  ],
  [
    "Do I have to apply, or can I just sign up?",
    "Just sign up — the 30-day trial is open to everyone, no application and no card. The form on this page is for operators who'd like the NestPlot team to plan and run their migration with them; it's optional. Either way you're on the same product and the same pricing.",
  ],
  [
    "Which integrations are included?",
    "All of them. Access: per-vertical vendor support — building fob and mobile-key partners for multifamily; PTI Falcon, SecurGuard, INSOMNIAC CIA, Noke, DaVinciLock, OpenTech IntelliGate, Janus for self-storage; LPR and QR-code validation for parking; gated dock and lot access for marina / RV. Accounting: QuickBooks Online, Xero, Sage Intacct (per LLC). Payments: Stripe Connect Express (customer payments direct to your bank). Identity: optional Stripe Identity verification before move-in. Certified mail: Lob.com for state-required notice delivery. Background checks: Checkr integration with a per-vertical package config. SMS: fully covered by NestPlot at any volume — never billed. Every NestPlot feature is included in the software subscription — the only two exceptions are Checkr background checks and Lob certified mail, which are charged to your saved card immediately per use at wholesale plus a small platform markup. Background checks specifically can be flipped to tenant-pays per facility, so the applicant pays via Stripe before the check runs — your call.",
  ],
  [
    "What are third-party pass-throughs?",
    "Two real, direct costs that NestPlot doesn't manufacture — background checks (Checkr, ~$30-50 per report depending on package) and certified mail for state-required lease + lien notices (Lob, ~$8 per letter). These are always billed at wholesale plus a small platform markup — your subscription covers the NestPlot software, not the third-party vendor bills. For background checks specifically, you decide per facility whether you eat the cost or pass it through to the applicant (a Stripe payment link they pay before the check runs). Certified mail defaults to your card. Outbound tenant SMS (Twilio) is fully covered by NestPlot — never billed to operators, even at high volumes.",
  ],
  [
    "Is my data safe if NestPlot disappears?",
    "Your customer data exports as CSV any time you want it. Stripe Connect means customer payments go to your bank account directly, not through us. If we vanish, your operation continues — you just lose the software dashboard.",
  ],
  [
    "What state-law coverage do you have?",
    "All 50 states + DC across every vertical. Statute-cited late-fee caps, grace periods, reminder cadences, and recovery-notice windows are wired into the core — landlord-tenant act for multifamily, self-storage statute for storage, parking lease law for lots, marina / RV agreement for vehicle storage. UETA / E-SIGN signing in every state; New York properties default to a stronger identity-binding step out of the box.",
  ],
] as const;

const STEPS = [
  {
    n: "01",
    title: "Tell us about your operation",
    body: "Which vertical you run, where the property is, roughly how many properties + total units / stalls / slips, what access hardware you use, and your current software. Two-line reply is plenty. Or skip straight to a trial — no application needed.",
  },
  {
    n: "02",
    title: "We plan your migration",
    body: "We reply within 1 business day with a migration plan: how your roster, leases, and balances come across, which access vendor we wire, and how Stripe Connect routes to your bank.",
  },
  {
    n: "03",
    title: "Migration session",
    body: "60–90 minutes, either at your property (NE operators) or screenshare. We import your customers and units, hook up your access vendor, and route Stripe to your bank — included, no setup fee.",
  },
  {
    n: "04",
    title: "Go live on your 30-day trial",
    body: "Run your property on NestPlot free for 30 days, no card. After that it's your standard plan rate — flat from $59/mo. Cancel any time, no questions.",
  },
];

export function PilotPage() {
  useSeo({
    title: "Switch to NestPlot — migration included, 30-day free trial",
    description:
      "Switch to NestPlot without the pain: the team runs your migration with you, included on every plan with no setup fee. 30-day free trial, no card. Multifamily, self-storage, parking, marina, RV/boat.",
    path: "/pilot",
    jsonLd: faqJsonLd(FAQ.map(([q, a]) => ({ q, a }))),
  });

  return (
    <div className="mx-auto max-w-[1180px] px-5 lg:px-8 py-12 lg:py-16">
      {/* Hero */}
      <div className="grid lg:grid-cols-[1.2fr,1fr] gap-10 items-start">
        <div>
          <Pill tone="accent" size="sm">
            Switch to NestPlot · migration included
          </Pill>
          <h1 className="t-display-xl mt-4">
            Switch without the pain.
          </h1>
          <p className="text-sub text-lg mt-4 max-w-[560px]">
            Operators across multifamily, self-storage, parking, marina,
            and RV/boat come to NestPlot to leave per-unit billing and
            processor lock-in behind. The team runs the migration with you
            — included on every plan, no setup fee — and you start on a
            30-day free trial with no card. After that it's flat pricing
            from $59/mo.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup">
              <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={14} />}>
                Start your 30 days
              </Btn>
            </Link>
            <a href="#apply">
              <Btn kind="secondary" size="lg">
                Plan my migration
              </Btn>
            </a>
          </div>
          <p className="text-sub text-[13px] font-mono uppercase tracking-[0.12em] mt-4">
            No card required · Migration included on every plan
          </p>
        </div>

        {/* Offer summary card */}
        <Card>
          <div className="relative">
            <Slats color="var(--accent)" opacity={0.7} height={10} />
          </div>
          <div className="px-5 py-6 space-y-4">
            <div>
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                What you get
              </div>
              <ul className="mt-2 space-y-2 text-[14px]">
                {[
                  "30-day free trial — no card required",
                  "Migration included on every plan — no setup fee",
                  "Flat pricing from $59/mo — Starter $59, Growth $99, Portfolio $199",
                  "All-50-states + DC lease engine with a court-ready audit trail",
                  "Stripe Connect — customer payments → your bank, we never touch them",
                  "Per-vertical access vendor support — building, gate, LPR, dock",
                  "Multi-entity accounting (QuickBooks, Xero, Sage Intacct)",
                  "Optional Stripe Identity verification before move-in",
                  "State-compliant recovery workflows with certified mail",
                  "CSV export anytime — your data is yours",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <Icon name="check" size={14} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-line pt-4">
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                The math
              </div>
              <div className="mt-1 font-mono text-[14px] leading-[1.7]">
                Trial · <strong>30 days, $0</strong>
                <br />
                Then · from <strong>$59/mo</strong>
                <br />
                Migration · <strong>included</strong>
              </div>
            </div>
            <div className="border-t border-line pt-4">
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                Your migration includes
              </div>
              <ul className="mt-2 space-y-1.5 text-[13.5px]">
                {[
                  "Roster, lease + balance import from your current software",
                  "Per-state lease-template review",
                  "Access-vendor handshake + Stripe Connect wiring",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <Icon name="arrow-right" size={12} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* How it works */}
      <section className="mt-16">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          How it works
        </div>
        <h2 className="font-display font-extrabold text-[28px] uppercase tracking-[-0.015em] mt-1">
          From your current software to a running property
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {STEPS.map((s) => (
            <Card key={s.n}>
              <div className="px-5 py-5">
                <div className="font-mono text-[11px] text-sub tracking-[0.14em]">
                  {s.n}
                </div>
                <div className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em] mt-2">
                  {s.title}
                </div>
                <p className="text-sub text-sm mt-2 leading-[1.5]">{s.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="mt-16 grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="px-5 py-5">
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              Who it's for
            </div>
            <ul className="mt-3 space-y-2 text-[14px]">
              {[
                "Operators of every size — from a single landlord to a nationwide portfolio — who want multi-entity accounting, team permissions, and a cross-property portfolio dashboard",
                "Currently paying for software you want to leave — AppFolio, Yardi, DoorLoop, storEDGE, SiteLink, Easy Storage Solutions, DockMaster, MarinaOffice, ParkMobile exports — or running on a spreadsheet you've outgrown",
                "Multi-LLC operators who track per-entity books in QuickBooks, Xero, or Sage Intacct",
                "Operators frustrated with enterprise sales cycles, support tickets that disappear, or processor lock-in",
                "Anyone running multiple verticals (e.g. storage + parking, or multifamily + RV) tired of separate stacks",
                "New England operators get priority for in-person setup — our team is in Burlington, MA; operators elsewhere are set up remotely",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Icon name="check" size={14} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
        <Card>
          <div className="px-5 py-5">
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              Probably not a fit
            </div>
            <ul className="mt-3 space-y-2 text-[14px]">
              {[
                "Operators locked into a long-term contract with their current software — we'll be there when it's up for renewal",
                "Operators whose access vendor isn't on our supported list (reach out — we add vendors based on operator demand)",
                "Operators expecting white-glove ongoing support — we run the migration with you, then it's email + in-app with a 1-hour business-hour response SLA.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Icon name="close" size={14} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </section>

      {/* FAQ */}
      <section className="mt-16">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Questions we hear
        </div>
        <h2 className="font-display font-extrabold text-[28px] uppercase tracking-[-0.015em] mt-1">
          FAQ
        </h2>
        <div className="mt-6 space-y-3">
          {FAQ.map(([q, a]) => (
            <details
              key={q}
              className="bg-paper border border-line rounded-md px-5 py-4"
            >
              <summary className="cursor-pointer font-semibold text-[15px]">
                {q}
              </summary>
              <p className="text-sub text-[14px] leading-[1.6] mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Inline "plan my migration" form — embedded so prospects don't
          have to click through a separate step. The hero CTA anchor-
          jumps here via #apply. Starting a trial needs no form (/signup);
          this is for operators who want the team to run their migration. */}
      <section className="mt-16">
        <div className="text-center mb-8">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Ready
          </div>
          <h2 className="font-display font-black text-[32px] lg:text-[40px] tracking-[-0.02em] mt-2">
            Plan your migration
          </h2>
          <p className="text-sub max-w-[560px] mx-auto mt-3">
            A few quick questions so we can plan how NestPlot takes over from
            your current software. We'll reply within 1 business day with a
            migration plan. Prefer to dive in first?{" "}
            <Link to="/signup" className="text-accent font-semibold">
              Start your 30-day trial
            </Link>
            .
          </p>
        </div>
        <PilotApplicationForm anchorId="apply" />
        <div className="text-center mt-6">
          <Link to="/contact" className="text-accent font-semibold text-sm">
            Have a question first? Contact us →
          </Link>
        </div>
      </section>
    </div>
  );
}
